import axios, { CancelTokenSource } from 'axios';
import { commonConstants } from 'constants/index';
import userHelpers from 'helpers/user';
import {
  GetSellerListExtraEnum,
  IGetPlatformConfigParams,
  IPlatform,
  ISeller,
  Website,
} from 'interfaces';
import { get, parseInt } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';
import { sellerServices, staffServices } from 'services';
import { IUserDetailsInfo } from 'teko-oauth2';

const {
  getCurrentPlatform,
  setCurrentPlatform,
  getCurrentSeller,
  setCurrentSeller,
} = userHelpers;

const { DEFAULT_NUMBER_DISPLAY_FORMAT } = commonConstants;

const useValidatedPlatformInfo = (currentUser?: IUserDetailsInfo) => {
  const [isFetching, setIsFetching] = useState(true);
  const [platformInfo, setPlatformInfo] = useState<IPlatform>();
  const [isAssignedWithAllPlatforms, setIsAssignedWithAllPlatforms] = useState(
    false
  );

  const savedPlatformId = getCurrentPlatform();

  const validatePlatform = async (
    platformId?: string
  ): Promise<IPlatform | undefined> => {
    const platformIdInt = parseInt(platformId || '');
    if (!platformIdInt) return undefined;

    try {
      const resp = await staffServices.getPlatformConfig({
        platformId: platformIdInt,
      });
      return resp;
    } catch (error) {
      return undefined;
    }
  };

  const updatePlatformInfo = useCallback(
    (platform?: IPlatform) => {
      if (platform) {
        setPlatformInfo(platform);
        setCurrentPlatform(platform.id);
      }
    },
    [setPlatformInfo, setCurrentPlatform]
  );

  useEffect(() => {
    // If no current user, do nothing
    if (!currentUser) return;
    // Fall back to -1 if user has not assigned
    const assignedPlatformId = String(
      get(currentUser, ['meta_data', 'platformId'], -1)
    );

    (async () => {
      try {
        // When user has not assigned any platformId
        // Do not save any platformId and info
        if (assignedPlatformId === '-1') {
          updatePlatformInfo(undefined);
          return;
        }

        // When user has assigned ALL platforms (0)
        // Check if there is any saved platformId in the local storage, if yes validate it (similar to the above case)
        // If not, do not save any platformId (let user select manually)
        if (assignedPlatformId === '0') {
          setIsAssignedWithAllPlatforms(true);

          const platform = await validatePlatform(savedPlatformId);
          updatePlatformInfo(platform);
          return;
        }

        // When user has assigned SPECIFIC platform => validate if the ID is valid
        if (assignedPlatformId && assignedPlatformId !== '0') {
          // Call API to get the platform info
          // If the platformId is valid, update the platform info and saved it
          const platform = await validatePlatform(assignedPlatformId);
          updatePlatformInfo(platform);
          return;
        }
      } finally {
        setIsFetching(false);
      }
    })();
  }, [currentUser]);

  return {
    isFetching,
    platformInfo,
    isAssignedWithAllPlatforms,
  };
};

const useWebsites = (currentUser?: IUserDetailsInfo) => {
  const [websites, setWebsites] = useState<Website[]>([]);
  const [fetchingWebsites, setFetchingWebsites] = useState<boolean>(false);

  useEffect(() => {
    const userPlatformId = currentUser?.meta_data.platformId;
    const isMerchantUser = !!currentUser?.meta_data.merchant_id;

    if (isMerchantUser && userPlatformId) {
      setFetchingWebsites(true);
      staffServices
        .getWebsitesByPlatformId(userPlatformId)
        .then(value => setWebsites(value))
        .finally(() => {
          setFetchingWebsites(false);
        });
    }
  }, [currentUser]);

  return {
    websites,
    fetchingWebsites,
  };
};

const useValidatedSellerInfo = (
  isFetchingPlatforms: boolean,
  platformInfo: IPlatform | undefined,
  currentUser: IUserDetailsInfo | undefined
) => {
  const [sellerInfo, setSellerInfo] = useState<ISeller>({} as ISeller);
  const savedSeller = getCurrentSeller();
  const { isFetching: isFetchingSellers, authorizedSellers } = useAvailSellers(
    isFetchingPlatforms,
    currentUser,
    platformInfo?.id
  );

  const validateSeller = useCallback(
    (sellerId?: string) => {
      if (!sellerId || !authorizedSellers.length) return;
      // Check if sellerId is valid and exists in availSellers
      const seller = authorizedSellers.find(
        seller => seller.id === parseInt(sellerId)
      );
      if (seller) {
        setSellerInfo(seller);
        setCurrentSeller(seller.id);
      }
    },
    [authorizedSellers, setSellerInfo, setCurrentSeller]
  );

  useEffect(() => {
    if (!currentUser) return;

    const assignedSellerIds = `${get(
      currentUser,
      ['meta_data', 'seller_id'],
      ''
    )}`
      .trim()
      .split(',')
      .filter(string => string !== '');

    // If user assigned with single, specific seller id, validate that seller
    if (assignedSellerIds.length === 1 && assignedSellerIds[0] !== '0') {
      validateSeller(assignedSellerIds[0]);
      return;
    }

    if (assignedSellerIds[0] === '0' || assignedSellerIds.length > 1) {
      validateSeller(savedSeller);
      return;
    }

    if (assignedSellerIds.length === 0) {
      return;
    }
  }, [currentUser, authorizedSellers]);

  return {
    isFetchingSellers,
    authorizedSellers,
    sellerInfo,
  };
};

// Fetch the platform config for the current seller (used for seller-specific number
// display format). Gated by the EPIC_ECOM_489 flag, so it must run after feature flags
// have been evaluated for the resolved seller.
const usePlatformConfigBySeller = (
  sellerId?: number,
  enableFlagToGetPlatformConfig?: boolean,
  isFlagInitiating?: boolean
) => {
  const [platformConfigBySeller, setPlatformConfigBySeller] = useState<
    IPlatform
  >({
    id: 0,
    name: '',
    numberDisplayFormat: DEFAULT_NUMBER_DISPLAY_FORMAT,
  });

  useEffect(() => {
    const getPlatformConfigBySeller = async (
      params: IGetPlatformConfigParams
    ) => {
      try {
        const platformConfig = await staffServices.getPlatformConfig(params);
        if (platformConfig?.numberDisplayFormat?.thousandSeparator) {
          setPlatformConfigBySeller(platformConfig);
        }
      } catch {}
    };

    if (!!sellerId && enableFlagToGetPlatformConfig && !isFlagInitiating) {
      getPlatformConfigBySeller({ sellerId });
    }
  }, [sellerId, enableFlagToGetPlatformConfig, isFlagInitiating]);

  return { platformConfigBySeller };
};

const useAvailSellers = (
  fetchingPlatforms: boolean,
  currentUser?: IUserDetailsInfo,
  validatedPlatformID?: IPlatform['id']
) => {
  const [isFetching, setIsFetching] = useState(true);
  const [authorizedSellers, setAuthorizedSellers] = useState<ISeller[]>([]);
  const filteredPlatformIds = useRef<{
    assigned: { platformId: number | undefined; validated: boolean };
    validated: { platformId: number | undefined; validated: boolean };
  }>();
  // To cancel previous request if there is any
  const cancelToken = useRef<CancelTokenSource | null>(null);

  // Strategy to fetch avail sellers:
  // First: use user's meta_data platformId to fetch sellers
  // After that, when fetchingPlatforms is false (fetched done) and platformId (selected) is different from meta_data platformId, fetch again with the new platformId
  // Since user platfromId is only used when user assigned with specific platform, so if user assigned with all platforms (0) or not assigned any platform (-1), do not fetch sellers
  useEffect(() => {
    if (!currentUser) return;

    const getAvailSellers = async (selectedPlatformId?: number) => {
      if (cancelToken.current) {
        cancelToken.current.cancel('Operation canceled due to new request.');
      }

      // Create a new cancel token for the new request
      cancelToken.current = axios.CancelToken.source();

      try {
        const assignedSellerIds = `${get(
          currentUser,
          ['meta_data', 'seller_id'],
          ''
        )}`
          .trim()
          .split(',')
          .filter(string => string !== '');
        const isAllSeller = assignedSellerIds?.[0] === '0';
        if (!assignedSellerIds.length) return;

        const sellers = await sellerServices.getAllSellersList(
          {
            platformIds: selectedPlatformId
              ? `${selectedPlatformId}`
              : undefined,
            ids: isAllSeller ? undefined : assignedSellerIds.join(','),
            extra: GetSellerListExtraEnum.ExcludeFilterCreatedBySeller,
          },
          assignedSellerIds?.[0] || '0',
          cancelToken.current.token
        );

        if (sellers.length) {
          setAuthorizedSellers(sellers);
        }
      } catch (error) {
        // If the request was cancelled, do not update the state
        if (axios.isCancel(error)) return;

        // Handle error (API error, network error, etc)
        setAuthorizedSellers([]);
      } finally {
        setIsFetching(false);
      }
    };

    // First use assigned platformId to fetch sellers
    if (
      fetchingPlatforms &&
      !filteredPlatformIds.current?.assigned?.validated
    ) {
      const metaDataPlatformId: number = get(
        currentUser,
        ['meta_data', 'platformId'],
        -1
      );
      // When user is assigned with all platform (0) or not assigned (-1) filter sellers without platformId
      // When user is assigned with specific platform, fetch sellers with that platformId
      const assignedPlatformId =
        metaDataPlatformId === -1 ||
        metaDataPlatformId === 0 ||
        metaDataPlatformId === null
          ? undefined
          : metaDataPlatformId;
      getAvailSellers(assignedPlatformId);

      // Update ref to prevent fetching again
      filteredPlatformIds.current = {
        assigned: { platformId: assignedPlatformId, validated: true },
        validated: { platformId: undefined, validated: false },
      };
    }

    // After that, when fetchingPlatforms is false (fetched done) and platformId (selected) is different from meta_data platformId, fetch again with the new platformId
    else if (
      // Done fetching platforms
      !fetchingPlatforms &&
      // Have fetched sellers with assigned platformId
      filteredPlatformIds.current?.assigned?.validated &&
      // Not yet fetched sellers with validated platformId
      !filteredPlatformIds.current?.validated?.validated &&
      // validated platformId is different from assigned platformId
      validatedPlatformID !== filteredPlatformIds.current?.assigned?.platformId
    ) {
      getAvailSellers(validatedPlatformID || undefined);
      // Update ref to prevent fetching again
      filteredPlatformIds.current.validated = {
        platformId: validatedPlatformID,
        validated: true,
      };
    }
  }, [fetchingPlatforms, currentUser, validatedPlatformID]);

  return {
    isFetching,
    authorizedSellers,
  };
};

export default {
  useWebsites,
  useValidatedPlatformInfo,
  useValidatedSellerInfo,
  usePlatformConfigBySeller,
};
